import React, { useContext, useState, useEffect, useRef } from 'react';
import { useDebounce } from 'react-use';

import { AutoComplete, Select } from 'antd';
import { groupBy } from 'lodash';

import { deaccentizeandlowerize, getHighlightedText } from '@libs/NwUtils';

import { useLayoutContext } from '@contexts/LayoutContext';
import { ListContext } from '@contexts/ListContext';
import { UserContext } from '@contexts/UserContext';

import EmptyState from '@components/Gui/EmptyState';
import NwIcon from '@components/Gui/NwIcon';
import { ModelCover } from '@components/ModelCover';

import { HeaderContext } from '../NwHeader';
import {
    StyledOption,
    StyledAutocompleteContainer,
    GlobalSearchWrapperContainer,
    GlobalSearchWrapper,
} from './GlobalSearchUI';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Option = AutoComplete.Option;
const OptGroup = Select.OptGroup;

const GlobalSearch = ({ closable, sources, onSearchContact }) => {
    const { currentUser } = useContext(UserContext);
    const headerContextValues = useContext(HeaderContext);
    const { activeSearch, onViewSearch } = headerContextValues;

    const layoutContext = useLayoutContext();
    const { isMobile, breakpoints } = layoutContext;

    const [filteredDataSource, setFilteredDataSource] = useState([]);
    const [currentValue, setCurrentValue] = useState('');
    const searchRef = useRef();
    const { cachedListLoaded } = useContext(ListContext);
    const [nameFilter, setNameFilter] = useState('');

    useDebounce(
        () => {
            setNameFilter(currentValue);
        },
        500,
        [currentValue]
    );

    const handleKeyDown = (event) => {
        if (event.keyCode === 27) {
            onViewSearch && onViewSearch(false);
            setCurrentValue('');
            if (searchRef && searchRef.current) {
                searchRef.current.blur();
            }
        }

        if (event.keyCode === 114 || (event.ctrlKey && event.keyCode === 70)) {
            event.preventDefault();
            onViewSearch && onViewSearch(true);
            searchRef.current.focus();
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown, false);
        return () => {
            document.removeEventListener('keydown', handleKeyDown, false);
        };

    }, []);

    const renderOptions = () => {
        if (filteredDataSource) {
            let sortedDataSource = [...filteredDataSource];
            sortedDataSource = sortedDataSource.sort((data1, data2) => {
                if (data1.searchValue.toLowerCase().indexOf(currentValue.toLowerCase()) === 0) return -1;
                if (data2.searchValue.toLowerCase().indexOf(currentValue.toLowerCase()) === 0) return 1;
                return 1;
            });
            const grouped = groupBy(sortedDataSource, (item) => item.searchGroup);
            //TODO: Working on sort function to sort grouped results by our specific rules

            const options = Object.keys(grouped)
                .sort((group1, group2) => {
                    if (group1 === 'models') return -1;
                    if (group2 === 'models') return 1;
                    return 1;
                })
                .map((group, groupIndex) => (
                    <OptGroup key={`opt-group-${groupIndex}`} label={group}>
                        {grouped[group].map((opt, index) => {
                            let item = null;
                            let url = '';
                            switch (group.toLowerCase()) {
                                case 'models':
                                case 'scouting':
                                    url = `/model/${opt.ID}`;
                                    item = (
                                        <StyledOption>
                                            {opt.cover && (
                                                <span
                                                    style={{
                                                        margin: '5px 20px',
                                                    }}
                                                >
                                                    <ModelCover
                                                        visibleByDefault
                                                        classN="model-cover-small"
                                                        model={{
                                                            CG: opt.cover,
                                                            ST: opt.subtype,
                                                        }}
                                                    />
                                                </span>
                                            )}
                                            <p>
                                                {getHighlightedText(opt.showValue.toLowerCase(), currentValue)}
                                                <br />
                                                {opt.secondLine && <small>{opt.secondLine}</small>}
                                            </p>
                                        </StyledOption>
                                    );
                                    break;
                                case 'clients':
                                    url = `/customer/${opt.ID}`;
                                    item = (
                                        <StyledOption>
                                            <NwIcon icon={byPrefixAndName.fasl['address-card']} />
                                            <p>{getHighlightedText(opt.showValue.toLowerCase(), currentValue)}</p>
                                        </StyledOption>
                                    );
                                    break;
                                case 'agencies':
                                    url = `/customer/${opt.ID}`;
                                    item = (
                                        <StyledOption>
                                            <NwIcon icon={byPrefixAndName.fasl['globe']} />
                                            <p>{getHighlightedText(opt.showValue.toLowerCase(), currentValue)}</p>
                                        </StyledOption>
                                    );
                                    break;
                                case 'services':
                                    url = `/customer/${opt.ID}`;
                                    item = (
                                        <StyledOption>
                                            <NwIcon icon={byPrefixAndName.fasl['life-ring']} />
                                            <p>{getHighlightedText(opt.showValue.toLowerCase(), currentValue)}</p>
                                        </StyledOption>
                                    );
                                    break;
                                default:
                                    break;
                            }

                            return (
                                <Option key={`${groupIndex}-${index}-${opt.ID}`} value={url}>
                                    {item}
                                </Option>
                            );
                        })}
                    </OptGroup>
                ));
            return options;
        } else {
            return [];
        }
    };

    const onChange = (selected) => {
        //Usually the parameter is an array, as in this case it will always be single selection
        //the value has been deconstructed to a single value taking just the first element of the array
        if (onViewSearch) {
            onViewSearch(false);
            searchRef.current.blur();
        }
        if (selected) {
            setCurrentValue('');
            const contactInfo = selected.split('/');
            onSearchContact({
                type: contactInfo[1],
                ID: Number(contactInfo[2]),
            });
        }
    };

    useEffect(() => {
        if (nameFilter.length >= 2 && sources && sources.length > 0) {
            const normalizedNameFilter = deaccentizeandlowerize(nameFilter);
            const filteredResult = sources.filter((item) => {
                return item.searchValue.includes(normalizedNameFilter);
            });
            setFilteredDataSource(filteredResult);
        } else {
            setFilteredDataSource([]);
        }
    }, [nameFilter, sources]);

    const handleSearch = (inputValue) => {
        setCurrentValue(inputValue);
    };

    const handleFocus = () => {
        if (!activeSearch) {
            onViewSearch(true);
        }
    };

    const isTablet = breakpoints === 2;

    const renderSearchContainer = () => {
        return currentUser ? (
            <GlobalSearchWrapperContainer $isMobile={isMobile} $isTablet={isTablet}>
                {cachedListLoaded && (
                    <StyledAutocompleteContainer $isMobile={isMobile} $isTablet={isTablet} activeSearch={activeSearch}>
                        {activeSearch && !isMobile && (
                            <NwIcon
                                style={{ marginRight: '1rem' }}
                                icon={byPrefixAndName.fasl['arrow-left']}
                                size="lg"
                                onClick={() => {
                                    setCurrentValue('');
                                    onViewSearch(false);
                                }}
                                pointer
                            />
                        )}
                        <div className="select-wrapper">
                            <NwIcon icon={byPrefixAndName.fasl['search']} />
                            <AutoComplete
                                autoComplete="off"
                                ref={searchRef}
                                className="global-search"
                                onSelect={onChange}
                                onSearch={handleSearch}
                                placeholder="Search in netwalk"
                                defaultOpen={false}
                                open={activeSearch}
                                value={currentValue}
                                notFoundContent={currentValue.length >= 2 && <EmptyState message="no results" />}
                                dropdownAlign={{ offset: [0, 0] }}
                                popupClassName={isMobile ? 'mobile-search-dropdown' : 'global-search-dropdown'}
                                dropdownMatchSelectWidth={false}
                                dropdownStyle={{
                                    width: isMobile ? '100%' : isTablet ? '18rem' : '25rem',
                                    maxHeight: '80vh',
                                }}
                                onFocus={handleFocus}
                                dropdownRender={(node) => {
                                    return <GlobalSearchWrapper>{node}</GlobalSearchWrapper>;
                                }}
                            >
                                {renderOptions()}
                            </AutoComplete>
                        </div>
                    </StyledAutocompleteContainer>
                )}
            </GlobalSearchWrapperContainer>
        ) : (
            <div />
        );
    };

    return breakpoints && breakpoints > 1 && breakpoints < 5 ? (
        activeSearch ? (
            renderSearchContainer()
        ) : (
            <div className="nw-global-search-icon-holder">
                <NwIcon
                    icon={byPrefixAndName.fasl['search']}
                    pointer
                    onClick={() => onViewSearch && onViewSearch(true)}
                />
            </div>
        )
    ) : (
        renderSearchContainer()
    );
};

export default GlobalSearch;
